import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { useCurrencyFormat } from '../hooks/useCurrencyFormat';
import { useRBAC } from '../hooks/useRBAC';
import { nekoPos } from 'public-contracts';
import CampaignService from '../services/campaignService';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Plus,
  Search,
  Edit,
  Eye,
  Trash2,
  Calendar,
  Target,
  TrendingUp,
} from 'lucide-react';
import { format } from 'date-fns';

type Campaign = nekoPos.Campaign;

const ModernCampaignList: React.FC = () => {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const { formatCurrency } = useCurrencyFormat();
  const { hasPermission } = useRBAC();
  const campaignService = useMemo(() => new CampaignService(), []);

  // Permission checks
  const canCreate = hasPermission('campaigns', 'create');
  const canEdit = hasPermission('campaigns', 'edit');
  const canDelete = hasPermission('campaigns', 'delete');
  const canViewReport = hasPermission('campaigns', 'view');

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await campaignService.getCampaigns();
      setCampaigns(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : translate('pos.campaigns.load_error')
      );
      console.error('Error loading campaigns:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(translate('pos.campaigns.delete_confirm'))) {
      return;
    }
    try {
      await campaignService.deleteCampaign(id);
      await loadCampaigns();
    } catch (err) {
      alert(translate('pos.campaigns.delete_error'));
      console.error('Error deleting campaign:', err);
    }
  };

  const filteredCampaigns = campaigns.filter(
    (campaign) =>
      campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCampaignStatus = (campaign: Campaign) => {
    const now = new Date();
    const startDate = new Date(campaign.startDate);
    const endDate = new Date(campaign.endDate);

    if (!campaign.isActive)
      return {
        label: translate('pos.campaigns.status.inactive'),
        color: 'gray',
      };
    if (now < startDate)
      return {
        label: translate('pos.campaigns.status.upcoming'),
        color: 'blue',
      };
    if (now > endDate)
      return {
        label: translate('pos.campaigns.status.ended'),
        color: 'gray',
      };
    return {
      label: translate('pos.campaigns.status.active'),
      color: 'green',
    };
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          {translate('pos.campaigns.loading')}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {translate('pos.campaigns.title')}
          </h1>
          <p className="text-muted-foreground">
            {translate('pos.campaigns.subtitle')}
          </p>
        </div>
        {canCreate && (
          <Button onClick={() => navigate('/campaigns/create')}>
            <Plus className="h-4 w-4 mr-2" />
            {translate('pos.campaigns.new_campaign')}
          </Button>
        )}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={translate(
                'pos.campaigns.search_placeholder'
              )}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Card className="border-red-500">
          <CardContent className="pt-6">
            <div className="text-red-500">{error}</div>
          </CardContent>
        </Card>
      )}

      {/* Campaigns Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredCampaigns.map((campaign) => {
          const status = getCampaignStatus(campaign);
          return (
            <Card
              key={campaign.id}
              className="hover:shadow-lg transition-shadow"
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <CardTitle className="text-lg">{campaign.name}</CardTitle>
                  <Badge variant={status.color as any}>{status.label}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {campaign.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {campaign.description}
                  </p>
                )}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {format(new Date(campaign.startDate), 'MMM d, yyyy')} -{' '}
                      {format(new Date(campaign.endDate), 'MMM d, yyyy')}
                    </span>
                  </div>
                  {campaign.targetRevenue && (
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-muted-foreground" />
                      <span>
                        {translate('pos.campaigns.fields.target')}:{' '}
                        <span data-sensitive-field="amount">
                          {formatCurrency(campaign.targetRevenue)}
                        </span>
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 pt-2">
                  {canViewReport && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        navigate(`/report/campaigns/${campaign.id}`)
                      }
                      className="flex-1"
                    >
                      <TrendingUp className="h-4 w-4 mr-2" />
                      {translate('pos.campaigns.actions.report')}
                    </Button>
                  )}
                  {canEdit && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/campaigns/${campaign.id}/edit`)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(campaign.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredCampaigns.length === 0 && !loading && (
        <Card>
          <CardContent className="pt-6 text-center text-muted-foreground">
            {searchTerm
              ? translate('pos.campaigns.no_results')
              : translate('pos.campaigns.no_campaigns')}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ModernCampaignList;
